import { DynamicPriceRate, FeeLine, toDynamicPriceRate } from "@/offering/api";
import { toFeeLine } from "@/offering/hooks/use_quote_query";
import { addHours } from "../../offering/time_utils";
import { Location, BaseLocation } from "./location";
import { SpaceType } from "@/data";

enum TemplateName {
  DayPass = "DayPass",
  MultiPass = "MultiPass",
  EmbedDayPass = "EmbedDayPass",
  EmbedDayOffice = "EmbedDayOffice",
  EmbedMeetingRoom = "EmbedMeetingRoom",
  MultiDayOffice = "MultiDayOffice",
}

export interface OrderInput {
  fullName: string;
  phoneNumber: string;
  email: string;
  startDate?: string;
  endDate?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  expirationDate?: string;
  quoteId?: string;
  ignorePayment?: boolean;
  companyName?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  taxNumber?: string;
}

export enum OrderStatus {
  Created = "created",
  Paid = "paid",
  Canceled = "canceled",
  Confirmed = "confirmed",
  Chargeback = "chargeback",
  Rejected = "rejected",
  DisputePending = "dispute_pending",
  DisputeWon = "dispute_won",
  DisputeLost = "dispute_lost",
}

export enum OrderableType {
  Booking = "Booking",
  AddOn = "AddOn",
  Membership = "Membership",
}

export enum BookingType {
  Hourly = "Hourly",
  Daily = "Daily",
}

export enum DiscountType {
  FixedPrice = "fixed_price",
  Amount = "amount",
  Percentage = "percentage",
  OriginalPrice = "original_price",
}

export enum OrderViewOption {
  ByUsageDate = "usageDate",
  ByCreatedDate = "createdDate",
}

export enum DateSelectOptions {
  Next30Days = "next30Days",
  Next7Days = "next7Days",
  Last7Days = "last7Days",
  Last30Days = "last30Days",
  Custom = "custom",
  AllTime = "allTime",
}

export enum AccessableType {
  OrderLineItem = "OrderLineItem",
  Subscription = "Subscription",
}

interface OrderItemPrice {
  startDate: string;
  endDate: string;
  dynamicPriceRate?: DynamicPriceRate;
}

export interface OrderItemBase {
  ID: string;
  quantity: number;
  orderableID: string;
  orderID: string;
  orderItemID: string;
  locationID: string;
  orderConfirmationID: string;
  orderName: string;
  orderCurrency: string;
  orderEmail: string;
  orderTotalPrice: number;
  orderTotalCumulativePrice: number | null;
  subscriptionFirstPaymentAmount: number;
  productType: {
    id: string;
    name: string;
  };
  status: OrderStatus;
  createdAt: Date;
  orderMarketingConsent?: boolean;
  dynamicPriceRate?: DynamicPriceRate;
  originalUnitPrice: number;
  originalSubTotal: number;
  unitPrice: number;
  subTotal: number;
  promotionCode?: string | null;
  accessableType?: AccessableType;
  accessableId?: string;
  orderItemPrices?: OrderItemPrice[];
  feeLines?: FeeLine[];
}

export interface OrderItemDayPass extends OrderItemBase {
  type: OrderableType.Booking;
  inventoryExceededBy: number;
  details: OrderItemDayPassDetails;
  orderable: {
    spaceType: SpaceType;
  };
}

export interface OrderItemHourlyPass extends OrderItemBase {
  type: OrderableType.Booking;
  details: OrderItemHourlyPassDetails;
  orderable: {
    spaceType: SpaceType;
  };
}

interface OrderItemHourlyPassDetails {
  type: BookingType.Hourly;
  date: string;
  hours: number;
  startTime: string;
  endTime: string;
  spaceID: string;
  attendees?: number;
  accessID?: string;
  accessName?: string;
  accessNumberOfEntries?: string;
  layoutID: string;
  eventTypeName?: string;
  eventTypeDescription?: string;
}

interface OrderItemDayPassDetails {
  type: BookingType.Daily;
  startDate: string;
  endDate: string;
  spaceID: string;
  layoutID: string;
  days: number;
  physicalSpaceID?: string;
  physicalSpace?: {
    id: string;
    floor: string;
    name: string;
  };
  attendees?: number;
  accessID?: string;
  accessName?: string;
  accessNumberOfEntries?: string;
  eventTypeName?: string;
  eventTypeDescription?: string;
}

export enum LocatableType {
  Location = "Location",
  GeoMarket = "GeoMarket",
  Partner = "SpacePartner",
}

export interface GeoMarket {
  id: string;
  name: string;
  description: string;
  locations: Location[];
}

interface MembershipOrderable {
  id: string;
  name: string;
  partnerID: string;
  locatableType: LocatableType;
  locatable?: Location | GeoMarket;
  timezone: string;
  currency: string;
  monthlyPrice: number;
  description: string;
  productCategory: string;
}

function toMembershipOrderable(data: any): MembershipOrderable {
  let locatable: Location | GeoMarket | undefined;
  if (!data || !data.locatable) {
    locatable = undefined;
  } else if (data.locatable_type === LocatableType.Location) {
    locatable = {
      ...(data.locatable || {}),
      city: data.locatable?.city,
      currencyCode: data.locatable?.currency,
      streetAddress: data.locatable?.street_address,
    };
  } else if (data.locatable_type === LocatableType.GeoMarket) {
    locatable = {
      id: data.id,
      name: data.locatable.name,
      description: data.locatable.description!,
      locations: data.locatable.locations.map((location: any) => ({
        ...(location || {}),
        city: location?.city,
        currencyCode: location?.currency,
        streetAddress: location?.street_address,
      })),
    };
  }

  return {
    id: data.id,
    name: data.name,
    partnerID: data.partner_id,
    currency: data.currency,
    description: data.description,
    locatable,
    locatableType: data.locatable_type,
    timezone: data.time_zone,
    monthlyPrice: Number(data.monthly_price),
    productCategory: data.product_category,
  };
}

export interface OrderItemMembership extends OrderItemBase {
  type: OrderableType.Membership; // TODO verify this membership type
  inventoryExceededBy: number;
  orderable?: MembershipOrderable;
}

export enum AddOnType {
  MultiPass = "MultiPass",
}

interface OrderItemMultiPassDetails {
  type: AddOnType.MultiPass;
  addOnType: string;
  spaceID: string;
  addOnID: string;
  name: string;
  description: string;
  numberOfEntries: number;
  daysToExpire: number;
}

export interface OrderItemMultiPass extends OrderItemBase {
  type: OrderableType.AddOn;
  details: OrderItemMultiPassDetails;
  orderable: {
    productCategory: string;
  };
}

export interface CreditCard {
  last4: string;
  network: string;
  networkLogoUrl: string | null;
}

export interface PaymentMethod {
  card: CreditCard | null;
}

export class APIError extends Error {}

export interface OrderBillingDetails {
  fullName?: string;
  phone?: string;
  email?: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  tax: string;
}

export interface TaxLine {
  amount: number;
  percentage: number;
  description: string;
}

export interface PromotionLine {
  amount: number;
  discount: number;
  itemPriceAfterDiscount: number;
  discountType: DiscountType;
  description: string;
  code: string;
}

export type OrderItem =
  | OrderItemDayPass
  | OrderItemHourlyPass
  | OrderItemMultiPass
  | OrderItemMembership;

export interface OrderLineItem {
  id: string;
  locationId: string;
  orderableType: OrderableType;
  usageDate?: string;
  productType: {
    id: string;
    name: string;
  };
  addOnType: string;
  membershipPlanPortal: boolean;
  subscriptionId: string;
}

export interface Order {
  id: string;
  currencyCode: string;
  subTotal: number;
  subTotalBeforeDiscount: number;
  subscriptionFirstPaymentAmount: number;
  totalPrice: number;
  totalPriceInUsd: number;
  stripeClientSecret: string;
  confirmationID: string;
  additionalNotes: string;
  status: OrderStatus;
  orderItems: OrderItem[];
  fullName: string;
  companyName: string;
  email: string;
  bookingReason: string;
  inviteID: string;
  phoneNumber: string;
  paidThrouTeamsSubscription: boolean;
  paymentMethodDetails: PaymentMethod | null;
  billingDetails: OrderBillingDetails | null;
  createdAt: Date;
  cancelledAt: string;
  taxLine: TaxLine | null;
  promotionLine: PromotionLine | null;
  source?: string;
  sourceLabel?: string;
  cancellable: boolean;
  uncancellableReason?: string;
  quoteId: string;
  currency: string;
  amendedOrders?: AmendedOrder[];
  feeLines?: FeeLine[];
  specialInstructions?: string;
  refundableAmount: number;
  rejectedReason?: string;
}

export interface AmendedOrder extends Order {
  orderItems: OrderItemHourlyPass[];
}

export interface MultiPassAccess {
  id: string;
  createdAt: number; // unix timestamp in sec
  numberOfEntries: number;
  expired: boolean; // false
  name: string;
  expirationDate: string;
  bookingsUsedCount: number;
  bookingsUnusedCount: number;

  // location space
  space: {
    id: string;
    name: string;
    thumbnailImage: string;
  };

  // location
  location: {
    id: string;
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };

  // orders
  order: {
    id: string;
    confirmationID: string;
    fullname: string;
    email: string;
    currency: string;
    quoteId: string;
  };

  // bookings
  bookings: MultiPassAccessBooking[];
}

export interface MultiPassAccessBooking {
  id: string;
  date: string;
  past: boolean; // in the future
  orderConfirmationID: string;
  orderID: string;
}

export interface CreateMultiPassBookingInput {
  date: string;
}

export interface PartnerUserProfile {
  userID: string;
  partnerID: string;
  partnerLocations: BaseLocation[];
  partnerName: string;
  partnerSlug: string | null;
  partnerOfferings: { id: string; templateName: TemplateName }[];
}

export interface PartnerLocationOrderLineItemsResponse {
  orderLineItems: OrderItem[];
  lastUpdatedAt?: string;
  loading?: boolean;
}

export interface OrderItemData {
  id: string;
  confirmationId: string;
  totalPrice: number;
  currency: string;
  status: OrderStatus;
  fullname: string;
  email: string;
  createdAt: Date;
  promotionCode: string;
  subscriptionFirstPaymentAmount: number;
  totalCumulativePrice: number;
  orderLineItems: OrderLineItem[];
}

export interface ManageOrderItemsData {
  orders?: OrderItemData[];
  paging: {
    currentPage: number;
    totalPage: number;
    perPage: number;
    total: number;
  };
  loading?: boolean;
}

export interface BookingsParam {
  startDate: string;
  endDate: string;
  viewOrderBy?: string;
  productType?: string;
  page?: number;
  perPage?: number;
  searchText?: string;
}

export function fromOrderInput(input: OrderInput) {
  return {
    fullname: input.fullName,
    phone: input.phoneNumber,
    email: input.email,
    start_date: input.startDate,
    expiration_date: input.expirationDate,
    quote_id: input.quoteId,
    ignore_payment: input.ignorePayment,
    company_name: input.companyName,
    street_address: input.streetAddress,
    city: input.city,
    state: input.state,
    country: input.country,
    postal_code: input.postalCode,
    tax_number: input.taxNumber,
  };
}

export function toMultiPassAccess(data: any): MultiPassAccess {
  return {
    id: data.id,
    createdAt: data.created_at,
    numberOfEntries: data.number_of_entries,
    expired: data.expired,
    name: data.name,
    expirationDate: data.expiration_date,
    bookingsUsedCount: data.bookings_used_count,
    bookingsUnusedCount: data.bookings_unused_count,
    space: {
      id: data.space.id,
      name: data.space.name,
      thumbnailImage: data.space.thumbnail_image,
    },
    location: {
      id: data.location.id,
      name: data.location.name,
      streetAddress: data.location.street_address,
      city: data.location.city,
      state: data.location.state,
      postalCode: data.location.postal_code,
      country: data.location.country,
    },
    order: {
      id: data.order.id,
      confirmationID: data.order.confirmation_id,
      fullname: data.order.fullname,
      email: data.order.email,
      currency: data.order.currency,
      quoteId: data.order.quote_id,
    },
    bookings: data.bookings.map(toMultiPassAccessBooking),
  };
}

export function toMultiPassAccessBooking(data: any) {
  return {
    id: data.id,
    date: data.date,
    past: data.past,
    orderConfirmationID: data.order_confirmation_id,
    orderID: data.order_id,
  };
}

export function toOrderItemDayPassDetails(data: any): OrderItemDayPassDetails {
  return {
    type: BookingType.Daily,
    attendees: data?.attendees,
    layoutID: data?.space_layout_id,
    spaceID: data?.space_id,
    startDate: data?.start_date,
    endDate: data?.end_date,
    days: data?.days,
    physicalSpace: data?.physical_space,
    physicalSpaceID: data?.physical_space_id,
    accessID: data?.access_id,
    accessName: data?.access_name,
    accessNumberOfEntries: data?.access_number_of_entries,
    eventTypeName: data?.event_type_name,
    eventTypeDescription: data?.event_type_description,
  };
}

export function toPartnerUserProfile(data: any) {
  return {
    userID: data.auth0_user_id,
    partnerID: data.partner_id,
    partnerLocations: data.partner_locations.map(toPartnerOfferingLocation),
    partnerName: data.partner_name,
    partnerInternalName: data.partner_internal_name,
    partnerSlug: data.partner_slug,
    partnerOfferings: data.partner_offerings.map(toPartnerOffering),
  };
}

function toPartnerOffering(data: any) {
  return {
    id: data.id,
    templateName: data.template_name,
  };
}

function toPartnerOfferingLocation(data: any) {
  return {
    id: data.location_id,
    name: data.location_name,
    streetAddress: data.location_street_address,
    city: data.location_city,
    timezone: data.location_time_zone,
    promoCodeBoxContent: data.promo_code_box_content,
    featuredPromoCode: data.featured_promo_code,
    generalInfo: data.general_info,
  };
}

function toOrderItemPrice(data: any): OrderItemPrice {
  return {
    startDate: data.start_date,
    endDate: data.end_date,
    dynamicPriceRate: data.dynamic_price_rate
      ? toDynamicPriceRate(data.dynamic_price_rate)
      : undefined,
  };
}

export function toOrderItem(data: any): OrderItem {
  const type: OrderableType = data.type || data.orderable_type;

  const base = {
    ID: data.id,
    orderableID: data.orderable_id,
    orderID: data.order_id,
    orderItemID: data.order_item_id,
    locationID: data.location_id,
    orderConfirmationID: data.order_confirmation_id,
    orderName: data.order_name,
    status: data.order_status,
    quantity: data.quantity,
    orderTotalPrice: data.order_total_price,
    orderTotalCumulativePrice: data.total_cumulative_price,
    subscriptionFirstPaymentAmount: data.subscription_first_payment_amount,
    orderEmail: data.order_email,
    orderCurrency: data.order_currency,
    productType: {
      id: data.product_type?.id,
      name: data.product_type?.name,
    },
    createdAt: new Date(data.created_at * 1000),
    orderMarketingConsent: !!data.order_marketing_consent,
    dynamicPriceRate: data.dynamic_price_rate
      ? toDynamicPriceRate(data.dynamic_price_rate)
      : undefined,
    originalUnitPrice: data.original_unit_price,
    originalSubTotal: data.original_sub_total,
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    promotionCode: data.promotion_code,
    accessableId: data.accessable_id,
    accessableType: data.accessable_type,
    orderItemPrices: data.order_item_prices?.map(toOrderItemPrice),
    feeLines: data.fee_lines?.map(toFeeLine),
  };

  if (type === OrderableType.AddOn) {
    return {
      ...base,
      type,
      details: toOrderItemMultiPassDetails(data.details),
      orderable: {
        productCategory: data.orderable.product_category,
      },
    };
  }

  if (type === OrderableType.Membership) {
    return {
      ...base,
      inventoryExceededBy: data.inventory_exceeded_by,
      orderable: data.orderable
        ? toMembershipOrderable(data.orderable)
        : undefined,
      type,
    };
  }

  if (data.details?.start_time) {
    return {
      ...base,
      type,
      details: toOrderItemHourlyPassDetails(data.details),
      orderable: {
        spaceType: data.orderable.space_type,
      },
    };
  }

  if (data.details?.time) {
    return {
      ...base,
      type,
      details: toLegacyOrderItemHourlyPassDetails(data.details),
      orderable: {
        spaceType: data.orderable.space_type,
      },
    };
  }

  return {
    ...base,
    type,
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    inventoryExceededBy: data?.inventory_exceeded_by,
    details: toOrderItemDayPassDetails(data.details),
    orderable: {
      spaceType: data.orderable.space_type,
    },
  };
}

export function toOrderItemV2(data: any): OrderLineItem {
  return {
    id: data.id,
    productType: {
      id: data.product_type?.id,
      name: data.product_type?.name,
    },
    locationId: data.location_id,
    usageDate: data.usage_date,
    orderableType: data.orderable_type,
    addOnType: data.details?.add_on_type,
    membershipPlanPortal: data.orderable?.membership_plan_portal,
    subscriptionId: data.subscription_id,
  };
}

export function toManageOrders(
  manageOrdersResponse: any
): ManageOrderItemsData {
  return {
    orders: manageOrdersResponse.data.map(toOrderItemData),
    paging: {
      currentPage: manageOrdersResponse.paging?.current_page,
      totalPage: manageOrdersResponse.paging?.total_page,
      perPage: manageOrdersResponse.paging?.per_page,
      total: manageOrdersResponse.paging?.total,
    },
  };
}

export function toOrderItemData(order: any): OrderItemData {
  return {
    id: order.id,
    confirmationId: order.confirmation_id,
    totalPrice: order.total_price,
    currency: order.currency,
    status: order.status,
    fullname: order.fullname,
    email: order.email,
    createdAt: new Date(order.created_at * 1000),
    promotionCode: order.promotion_code,
    subscriptionFirstPaymentAmount: order.subscription_first_payment_amount,
    totalCumulativePrice: order.total_cumulative_price,
    orderLineItems: order.order_line_items.map(toOrderItemV2),
  };
}

function toOrderItemMultiPassDetails(data: any): OrderItemMultiPassDetails {
  return {
    type: data?.type,
    addOnType: data?.add_on_type,
    spaceID: data?.space_id,
    addOnID: data?.add_on_id,
    name: data?.add_on_name,
    description: data?.add_on_description,
    numberOfEntries: data?.number_of_entries,
    daysToExpire: data?.days_to_expire,
  };
}

function toOrderItemHourlyPassDetails(data: any): OrderItemHourlyPassDetails {
  return {
    type: BookingType.Hourly,
    spaceID: data?.space_id,
    date: data?.date,
    hours: data?.hours,
    startTime: data?.start_time,
    endTime: data?.end_time,
    layoutID: data?.space_layout_id,
    attendees: data?.attendees,
    eventTypeName: data?.event_type_name,
    eventTypeDescription: data?.event_type_description,
  };
}

function toLegacyOrderItemHourlyPassDetails(
  data: any
): OrderItemHourlyPassDetails {
  return {
    type: BookingType.Hourly,
    spaceID: data?.space_id,
    date: data?.date,
    hours: data?.hours,
    startTime: data?.time,
    endTime: addHours(data?.time, data?.duration),
    layoutID: data?.space_layout_id,
  };
}

export function toOrder(data: any): Order {
  return {
    id: data.id,
    confirmationID: data.confirmation_id,
    status: data.status,
    subTotal: data.sub_total,
    subTotalBeforeDiscount: data.sub_total_before_discount,
    subscriptionFirstPaymentAmount: data.subscription_first_payment_amount,
    additionalNotes: data.additional_notes,
    currencyCode: data.currency,
    stripeClientSecret: data.stripe_client_secret,
    totalPrice: data.total_price,
    totalPriceInUsd: data.total_price_in_usd,
    fullName: data.fullname,
    email: data.email,
    bookingReason: data.booking_reason,
    companyName: data.company_name,
    inviteID: data.invite_id,
    phoneNumber: data.phone,
    paidThrouTeamsSubscription: data.paid_throu_teams_subscription,
    billingDetails: data.billing_details
      ? {
          fullName: data.billing_details.full_name,
          phone: data.billing_details.phone,
          email: data.billing_details.email,
          streetAddress: data.billing_details.street_address,
          city: data.billing_details.city,
          state: data.billing_details.state,
          postalCode: data.billing_details.postal_code,
          country: data.billing_details.country,
          tax: data.billing_details.tax_number,
        }
      : null,
    orderItems: data.order_items.map(toOrderItem),
    paymentMethodDetails: toPaymentMethod(data.payment_method_details),
    createdAt: new Date(data.created_at * 1000),
    taxLine: data.tax_line
      ? {
          amount: data.tax_line.amount,
          description: data.tax_line.description,
          percentage: data.tax_line.percentage,
        }
      : null,
    promotionLine: data.promotion_line
      ? {
          amount: data.promotion_line.amount,
          description: data.promotion_line.description,
          code: data.promotion_line.code,
          discount: data.promotion_line.discount,
          discountType: data.promotion_line.discount_type,
          itemPriceAfterDiscount: data.promotion_line.item_price_after_discount,
        }
      : null,
    source: data.source,
    sourceLabel: data.source_label,
    cancellable: data.cancellable,
    uncancellableReason: data.uncancellable_reason,
    cancelledAt: data.cancelled_at,
    quoteId: data.quote_id,
    currency: data.currency,
    amendedOrders: data.amended_orders?.map(toOrder),
    feeLines: data.fee_lines?.map(toFeeLine),
    specialInstructions: data.special_instructions,
    refundableAmount: data.refundable_amount,
    rejectedReason: data.rejected_reason,
  };
}

function toPaymentMethod(data: any): PaymentMethod | null {
  if (!data || !data.card) {
    return null;
  }

  return {
    card: {
      last4: data.card.last4,
      network: data.card.network,
      networkLogoUrl: data.card.network_logo_url,
    },
  };
}

export function getBookingOrderItem(order: Order) {
  const oi = order.orderItems.find(
    (oi) => "type" in oi && oi.type === OrderableType.Booking
  );

  return oi;
}

export function getMembershipOrderItem(order: Order): OrderItem | undefined {
  const membershipOrder = order.orderItems.find(
    (oi) => oi.type === OrderableType.Membership
  );

  if (!membershipOrder) {
    return undefined;
  }

  return membershipOrder;
}

export function getAddOnOrderItem(
  order: Order
): OrderItemMultiPass | undefined {
  const oi = order.orderItems.find((oi) => oi.type === OrderableType.AddOn);

  if (oi?.type !== OrderableType.AddOn) {
    return undefined;
  }

  return oi;
}

export function getMainOrderItem(order: Order) {
  return (
    getAddOnOrderItem(order) ||
    getBookingOrderItem(order) ||
    getMembershipOrderItem(order)
  );
}

export function displayStatus(status: OrderStatus) {
  switch (status) {
    case OrderStatus.Canceled:
      return "Canceled";
    case OrderStatus.Confirmed:
      return "Confirmed";
    case OrderStatus.Paid:
      return "Paid";
    case OrderStatus.Created:
      return "Created";
    case OrderStatus.Chargeback:
      return "Chargeback";
    case OrderStatus.Rejected:
      return "Rejected";
    case OrderStatus.DisputePending:
      return "Dispute Pending";
    case OrderStatus.DisputeWon:
      return "Dispute Won";
    case OrderStatus.DisputeLost:
      return "Dispute Lost";
    default:
      return "";
  }
}

interface CancelOrder {
  refundedAmount: number;
  currency: string;
  message: string;
  status: string;
}

export function toPartnerCancelOrder(data: any): CancelOrder {
  return {
    refundedAmount: data.refunded_amount,
    currency: data.currency,
    message: data.message,
    status: data.status,
  };
}
